import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import SEO from "@/components/SEO";
import RichContent from "@/components/RichContent";
import FacebookShareButton from "@/components/FacebookShareButton";

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { pick, lang } = useLang();
  const [post, setPost] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => nav("/blog")); }, [slug, nav]);

  if (!post) return <div className="min-h-screen bg-[#0D0D0D] pt-32 text-center text-white/50">Chargement…</div>;

  const dfmt = (iso) => iso ? new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : (lang === "nl" ? "nl-BE" : "fr-BE"), {day:"2-digit", month:"long", year:"numeric"}) : "";
  const content = pick(post.content);

  return (
    <div data-testid="blog-post" className="pt-24 pb-24 bg-[#0D0D0D] min-h-screen">
      <SEO title={pick(post.title)} description={pick(post.excerpt) || pick(post.title)} image={post.cover_image} type="article" />
      <article className="max-w-[900px] mx-auto px-6 lg:px-10">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C8A75A] mb-8">
          <ArrowLeft className="w-3 h-3" /> Retour au blog
        </Link>

        <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A] flex items-center gap-2 mb-4">
          <Calendar className="w-3 h-3" /> {dfmt(post.published_at || post.created_at)}
        </p>
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6 leading-tight">{pick(post.title)}</h1>
        {pick(post.excerpt) && <p className="text-white/70 text-lg leading-relaxed border-l-2 border-[#C8A75A] pl-6 my-8">{pick(post.excerpt)}</p>}
        <div className="my-6">
          <FacebookShareButton testid="blog-fb-share" />
        </div>

        {post.cover_image && (
          <div className="aspect-[16/9] rounded-md overflow-hidden my-10 bg-black">
            <img src={post.cover_image} alt={typeof post.title === "string" ? post.title : (post.title?.fr || "Article KV International")} fetchpriority="high" decoding="async" className="w-full h-full object-cover img-fade" />
          </div>
        )}

        <div className="prose-invert text-white/85 leading-relaxed text-lg space-y-6">
          <RichContent text={content} />
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap items-center gap-3">
          {post.tags?.map((tag, i) => (
            <span key={i} className="text-[10px] uppercase tracking-widest text-[#C8A75A] border border-[#C8A75A]/30 rounded-sm px-3 py-1">#{tag}</span>
          ))}
        </div>

        {/* Bloc partage — bien visible en bas d'article */}
        <div className="mt-12 bg-[#1E1E1E] border border-[#1877F2]/30 rounded-md p-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">Cet article vous a plu ?</p>
          <h3 className="font-display text-2xl md:text-3xl text-white font-light mb-6">Partagez-le avec vos passionnés.</h3>
          <FacebookShareButton testid="blog-fb-share-bottom" label="Partager sur Facebook" className="px-8 py-3.5" />
        </div>
      </article>
    </div>
  );
}
